import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    FaRegCalendarCheck,
    FaUsers,
    FaShoppingCart,
    FaMoneyCheckAlt,
    FaLock,
    FaChartLine,
    FaUserShield,
    FaMobileAlt,
} from "react-icons/fa";
import { MdFamilyRestroom, MdOutlineAddchart } from "react-icons/md";
import { motion, animate, useInView, useMotionValue } from "framer-motion";

import Comp1 from "../../resources/comp1.png";
import Comp2 from "../../resources/comp2_noshadow.png";
import logo from "../../resources/logo.png";


// ---------- FEATURE CARD ----------
const FeatureCard = ({ feature, onOpen }) => {
    const colorClasses = {
        custom_red: "bg-[#CA2121]",
        custom_blue: "bg-[#2B5BA3]",
        custom_green: "bg-[#33B246]",
        custom_yellow: "bg-[#D6C137]"
    };

    return (
        <motion.div
            whileHover={{ scale: 1.05 }}
            className={`${colorClasses[feature.color]} flex flex-col items-center p-6 rounded-3xl shadow-lg cursor-pointer relative overflow-hidden text-white transition duration-300`}
            onClick={onOpen}
        >
            <div className="p-4 mb-4 bg-white/20 rounded-2xl flex items-center justify-center">
                <feature.icon className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-center">{feature.title}</h3>

            {/* Ícone de “+” indicando que há mais */}
            <div className="absolute top-4 right-4 w-8 h-8 bg-white/25 rounded-full flex items-center justify-center text-white text-lg font-bold">
                +
            </div>
        </motion.div>
    );
};

// ---------- FEATURE MODAL ----------
const FeatureModal = ({ feature, isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full p-8 relative"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 text-2xl font-bold"
                >
                    ×
                </button>

                <div className="flex flex-col md:flex-row gap-6 items-center">
                    <img src={feature.image} alt={feature.title} className="w-full md:w-1/2 rounded-xl" />
                    <div className="md:w-1/2">
                        <h2 className="text-3xl font-bold mb-4 text-custom_blue">{feature.title}</h2>
                        <p className="text-gray-700 mb-4">{feature.description}</p>
                        {feature.details && (
                            <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                {feature.details.map((item, i) => (
                                    <li key={i}>{item}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

// ---------- ALL FEATURES SECTION ----------
const AllFeaturesSection = () => {
    const features = [
        { title: "Controle de Clientes", icon: FaUsers, color: "custom_blue", image: Comp2, description: "Gerencie os dados de seus alunos e responsáveis com facilidade.", details: ["Cadastro completo", "Filtros avançados", "Busca rápida"] },
        { title: "Vendas em caixa e compras web", icon: FaShoppingCart, color: "custom_green", image: Comp2, description: "Controle total de vendas e pedidos online, sem complicações.", details: ["Fluxo de caixa integrado", "Relatórios em tempo real", "Notificações automáticas"] },
        { title: "Gestão de Cardápio e Estoque", icon: MdOutlineAddchart, color: "custom_yellow", image: Comp2, description: "Mantenha cardápio e estoque sempre atualizados.", details: ["Alertas de baixa quantidade", "Relatórios de consumo", "Integração com compras"] },
        { title: "Fila de Pedidos", icon: MdFamilyRestroom, color: "custom_red", image: Comp2, description: "Organize os pedidos de forma eficiente, evitando atrasos.", details: ["Visualização em tempo real", "Notificações para responsáveis", "Prioridades de pedidos"] },
        { title: "Controle Financeiro", icon: FaMoneyCheckAlt, color: "custom_blue", image: Comp2, description: "Tenha total visibilidade de receitas e despesas.", details: ["Exportação de relatórios", "Integração bancária", "Fluxo de caixa detalhado"] },
        { title: "Métricas de Venda", icon: FaChartLine, color: "custom_green", image: Comp2, description: "Acompanhe o desempenho de vendas e identifique oportunidades.", details: ["Gráficos interativos", "Comparativos mensais", "Alertas de metas"] },
        { title: "Controle de Acesso de Usuários", icon: FaUserShield, color: "custom_yellow", image: Comp2, description: "Defina permissões e níveis de acesso para toda equipe.", details: ["Admin, operador e visualizador", "Segurança avançada", "Logs de acesso"] },
        { title: "Sistema de Notificações", icon: FaRegCalendarCheck, color: "custom_red", image: Comp2, description: "Mantenha todos informados sobre atividades importantes.", details: ["Notificações push e email", "Alertas de pagamentos", "Lembretes automáticos"] }
    ];

    const [selectedFeature, setSelectedFeature] = useState(null);

    return (
        <section className="py-28">
            <h2 className="text-4xl font-bold text-center text-gray-800 mb-12 font-montserrat">
                Funcionalidades completas do Foodz
            </h2>
            <div className="max-w-6xl mx-auto grid gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 px-6">
                {features.map((feature, i) => (
                    <FeatureCard key={i} feature={feature} onOpen={() => setSelectedFeature(feature)} />
                ))}
            </div>

            {selectedFeature && (
                <FeatureModal feature={selectedFeature} isOpen={!!selectedFeature} onClose={() => setSelectedFeature(null)} />
            )}
        </section>
    );
};

// ==========================================
// COMPONENTE: MÉTRICA ANIMADA
// ==========================================
const MetricCard = ({ icon: Icon, label, value, duration = 2 }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });
    const count = useMotionValue(0);
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        const unsubscribe = count.onChange((v) => setDisplay(Math.floor(v)));
        let control;
        if (isInView) {
            control = animate(count, value, { duration, ease: "easeOut" });
        }
        return () => {
            if (control) control.stop();
            unsubscribe();
        };
    }, [isInView, value, count, duration]);

    const formatted = display.toLocaleString("pt-BR");

    return (
        <motion.div
            ref={ref}
            className="bg-white/90 backdrop-blur-xl border border-gray-100 rounded-3xl shadow-xl p-8 flex flex-col items-center justify-center text-center hover:scale-105 hover:shadow-2xl transition-transform duration-500"
            whileHover={{ y: -5 }}
        >
            <div className="p-4 bg-custom_blue/10 text-custom_blue rounded-full mb-6 shadow-inner">
                <Icon className="w-12 h-12" />
            </div>
            <motion.span className="text-5xl font-extrabold text-gray-800">
                +{formatted}
            </motion.span>
            <span className="mt-2 text-lg font-medium text-gray-600">{label}</span>
        </motion.div>
    );
};


// ==========================================
// COMPONENTE: ROADMAP
// ==========================================
const Roadmap = ({ features }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    return (
        <div ref={ref} className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
            {features.map((feature, index) => (
                <div key={index} className="flex flex-col gap-2">
                    <span className="text-lg font-semibold text-gray-800">
                        {feature.name}
                    </span>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: isInView ? `${feature.progress}%` : 0 }}
                            transition={{
                                duration: 2.2,
                                ease: "easeInOut",
                                delay: index * 0.3,
                            }}
                            className="h-full bg-custom_blue rounded-full shadow-inner"
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

// ==========================================
// SEÇÃO: HERO COM CTA
// ==========================================
const HeroSection = () => (
    <section className="relative w-full mt-24 px-6 py-20 overflow-hidden">
        <div className="text-center max-w-5xl mx-auto relative z-10">
            <h1 className="text-5xl font-bold text-gray-800 leading-tight">
                A revolução da cantina escolar:
                <br />
                <span className="text-custom_blue">gestão fácil, rápida e segura!</span>
            </h1>

            <p className="mt-10 text-xl text-gray-600 max-w-3xl mx-auto">
                Diga adeus às filas e à desorganização!
                <br />
                Nosso sistema permite um controle completo de vendas, pagamentos e
                estoque de forma simples e eficiente.
            </p>

            <div className="mt-16">
                <img
                    src={Comp1}
                    alt="Dashboard preview"
                    className="w-full lg:w-3/4 mx-auto rounded-2xl shadow-2xl"
                />
            </div>
        </div>

        <div className="absolute top-3/4 left-0 w-full h-1/2 bg-gradient-to-r from-custom_blue to-custom_green z-0"></div>
    </section>
);

// ==========================================
// SEÇÃO: BENEFITS COM CTA
// ==========================================
const BenefitsSection = () => {
    const benefits = [
        { color: "from-custom_green to-custom_green/90", icon: FaChartLine, title: "Performance e resultados", text: "Tenha relatórios precisos e insights valiosos para tomar decisões inteligentes e aumentar suas vendas." },
        { color: "from-custom_yellow to-yellow-400", icon: FaLock, title: "Confiabilidade e transparência", text: "Operações seguras, pagamentos automáticos e controle total sobre os movimentos da sua cantina." },
        { color: "from-custom_red to-red-500", icon: FaRegCalendarCheck, title: "Praticidade no dia a dia", text: "Simplifique processos com automação e acompanhamento em tempo real de tudo o que acontece." },
    ];

    return (
        <section className="py-28">
            <h2 className="text-center text-4xl font-bold mb-16 text-gray-800 font-montserrat">
                Algumas de nossas vantagens
            </h2>

            <div className="max-w-6xl mx-auto grid gap-10 md:grid-cols-3 px-6">
                {benefits.map(({ color, icon: Icon, title, text }, i) => (
                    <motion.div
                        key={i}
                        whileHover={{ scale: 1.05 }}
                        className={`p-10 text-white rounded-3xl shadow-xl bg-gradient-to-br ${color} transition-all duration-500 hover:brightness-110 hover:shadow-2xl flex flex-col items-start`}
                    >
                        <div className="p-4 bg-white/20 rounded-2xl mb-6">
                            <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-semibold mb-3">{title}</h3>
                        <p className="text-white/90 leading-relaxed">{text}</p>
                    </motion.div>
                ))}
            </div>

            {/* CTA Benefits */}
            <div className="mt-12 text-center">
                <Link to="/contato">
                    <button className="px-8 py-4 rounded-full bg-custom_blue text-white font-semibold hover:bg-custom_red/90 transition">
                        Agendar Demonstração Gratuita
                    </button>
                </Link>
            </div>
        </section>
    );
};

// ==========================================
// CTA FLUTUANTE FIXO
// ==========================================
const FloatingCTA = () => (
    <Link to="/contato">
        <motion.div
            whileHover={{ scale: 1.05 }}
            className="fixed bottom-6 right-6 bg-custom_blue text-white font-semibold px-6 py-4 rounded-full shadow-xl cursor-pointer z-50 hover:bg-custom_blue/90 transition"
        >
            Agendar Demo
        </motion.div>
    </Link>
);


// ==========================================
// SEÇÃO: FUNCIONALIDADES (com imagem)
// ==========================================
const FeaturesSection = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    const features = [
        {
            icon: FaRegCalendarCheck,
            title: "Gestão completa e automatizada",
            text: "Controle pedidos, estoques e pagamentos de forma integrada, em tempo real e sem complicações.",
        },
        {
            icon: FaMobileAlt,
            title: "Acesso em qualquer dispositivo",
            text: "Totalmente online — acesse via computador, tablet ou celular com a mesma experiência fluida.",
        },
        {
            icon: FaUserShield,
            title: "Segurança e privacidade",
            text: "Todos os dados são protegidos com criptografia e padrões de segurança de nível corporativo.",
        },
    ];

    return (
        <section
            ref={ref}
            className="w-full bg-white py-28 px-6 flex flex-col items-center"
        >
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <h2 className="text-4xl font-montserrat font-bold text-gray-800 mb-6">
                        ✨ Funcionalidades que fazem a diferença ✨
                    </h2>
                    <p className="text-lg text-gray-600 mb-12">
                        Tudo o que sua cantina precisa para operar de forma moderna, segura
                        e automatizada — em um único lugar.
                    </p>

                    <div className="flex flex-col gap-8">
                        {features.map((f, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -30 }}
                                animate={
                                    isInView ? { opacity: 1, x: 0, transition: { delay: i * 0.2 } } : {}
                                }
                                className="flex items-start gap-4"
                            >
                                <div className="p-4 bg-custom_blue/10 text-custom_blue rounded-2xl">
                                    <f.icon className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-1">
                                        {f.title}
                                    </h3>
                                    <p className="text-gray-600">{f.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0, transition: { duration: 0.8 } } : {}}
                    className="flex justify-center relative"
                >
                    <img
                        src={Comp2}
                        alt="Telas do sistema Foodz"
                        className="w-full max-w-lg rounded-2xl ring-b-1 ring-gray-200"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-40 rounded-2xl"></div>
                </motion.div>
            </div>
        </section>
    );
};

// ==========================================
// SEÇÃO: ROADMAP + MÉTRICAS + FOOTER
// ==========================================
const MetricsSection = () => {
    const metrics = [
        { icon: FaUsers, label: "alunos atendidos", value: 400 },
        { icon: FaShoppingCart, label: "pedidos realizados", value: 7500 },
        { icon: FaMoneyCheckAlt, label: "em vendas (R$)", value: 120000 },
    ];

    return (
        <section className="relative py-28 bg-gradient-to-br from-custom_blue via-custom_green to-custom_blue text-white">
            <div className="max-w-6xl mx-auto text-center px-6 relative z-10">
                <h2 className="text-4xl font-montserrat font-bold mb-6 tracking-tight">
                    Nossas conquistas até agora 🚀
                </h2>
                <p className="text-lg mb-16 opacity-90">
                    O Foodz já está transformando a rotina de escolas e cantinas.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
                    {metrics.map((metric, i) => (
                        <MetricCard key={i} {...metric} />
                    ))}
                </div>
            </div>

            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.25)_0%,_transparent_70%)]"></div>
        </section>
    );
};

const Footer = () => (
    <footer className="bg-gray-900 text-gray-300 py-12 text-center">
        <p>© {new Date().getFullYear()} Foodz. Todos os direitos reservados.</p>
        {/* TODO: add those links */}
        {/* <div className="mt-3 space-x-4 text-sm">
            <a href="#" className="hover:text-white transition">
                Política de Privacidade
            </a>
            <a href="#" className="hover:text-white transition">
                Termos de Uso
            </a>
            <a href="#demo" className="hover:text-white transition">
                Agendar Demo
            </a>
        </div> */}
    </footer>
);


// ==========================================
// PÁGINA PRINCIPAL
// ==========================================
export default function LandingPage() {
    const features = [
        { name: "MÓDULO RELATÓRIOS AVANÇADOS", progress: 80 },
        { name: "MÓDULO NUTRIÇÃO", progress: 50 },
        { name: "COMPRAS E FORNECEDORES", progress: 30 },
        { name: "MODO EVENTO", progress: 0 },
        { name: "INTEGRAÇÃO WHATSAPP", progress: 0 },
    ];

    return (
        <div className="flex flex-col w-full font-sans text-gray-900">
            {/* Header */}
            <header className="fixed top-0 left-0 w-full z-50 flex justify-between items-center px-6 py-3 border-b border-gray-100 backdrop-blur-lg bg-white/70 shadow-sm">
                <img src={logo} alt="Logo Foodz" className="w-28 sm:w-32" />
                <div className="flex space-x-3">
                    <Link to="/contato">
                        <button className="px-5 py-2 rounded-full bg-custom_blue text-white font-medium hover:bg-custom_blue/90 transition">
                            Fale Conosco
                        </button>
                    </Link>
                    <Link to="/login">
                        <button className="px-5 py-2 rounded-full border border-custom_blue text-custom_blue font-medium hover:bg-custom_blue/10 transition">
                            Login
                        </button>
                    </Link>
                </div>
            </header>

            {/* Seções */}
            <HeroSection />
            <FeaturesSection />

            <AllFeaturesSection />

            <section className="py-28 text-center">
                <h2 className="text-4xl font-bold text-gray-800 mb-6 font-montserrat">
                    Nosso roadmap
                </h2>
                <p className="text-lg text-gray-600 mb-16 max-w-3xl mx-auto">
                    Estamos constantemente evoluindo, com novas funcionalidades e
                    melhorias para transformar seu dia a dia.
                </p>
                <Roadmap features={features} />
            </section>

            <BenefitsSection />

            <MetricsSection />
            <FloatingCTA />
            <Footer />
        </div>
    );
}
